import React from 'react';

const ProgramExcellence = () => {
  const styles = {
    container: {
      minHeight: '100vh',
      background: 'linear-gradient(135deg, #f8fafc 0%, #f1f5f9 50%, #e2e8f0 100%)',
      fontFamily: 'Arial, sans-serif',
      padding: '60px 20px',
      color: '#1e293b'
    },

    header: {
      textAlign: 'center',
      marginBottom: '80px'
    },

    mainTitle: {
      fontSize: '3.5rem',
      fontWeight: 'bold',
      color: '#1e293b',
      marginBottom: '20px',
      lineHeight: '1.2'
    },

    highlightedText: {
      background: 'linear-gradient(135deg, #3b82f6, #1d4ed8)',
      WebkitBackgroundClip: 'text',
      WebkitTextFillColor: 'transparent',
      backgroundClip: 'text'
    },

    subtitle: {
      fontSize: '1.1rem',
      color: '#64748b',
      maxWidth: '800px',
      margin: '0 auto',
      lineHeight: '1.6'
    },

    featuresGrid: {
      display: 'grid',
      gridTemplateColumns: 'repeat(auto-fit, minmax(250px, 1fr))',
      gap: '30px',
      maxWidth: '1200px',
      margin: '0 auto 80px auto'
    },

    featureCard: {
      background: 'white',
      borderRadius: '16px',
      padding: '30px',
      textAlign: 'center',
      boxShadow: '0 4px 20px rgba(0, 0, 0, 0.05)',
      border: '1px solid #e2e8f0',
      transition: 'all 0.3s ease',
      cursor: 'pointer'
    },

    featureIcon: {
      width: '60px',
      height: '60px',
      borderRadius: '12px',
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      margin: '0 auto 20px auto',
      fontSize: '24px'
    },

    blueIcon: {
      background: 'linear-gradient(135deg, #dbeafe, #bfdbfe)',
      color: '#3b82f6'
    },

    purpleIcon: {
      background: 'linear-gradient(135deg, #e0e7ff, #c7d2fe)',
      color: '#8b5cf6'
    },

    greenIcon: {
      background: 'linear-gradient(135deg, #dcfce7, #bbf7d0)',
      color: '#10b981'
    },

    orangeIcon: {
      background: 'linear-gradient(135deg, #fed7aa, #fdba74)',
      color: '#f59e0b'
    },

    featureTitle: {
      fontSize: '1.25rem',
      fontWeight: 'bold',
      color: '#1e293b',
      marginBottom: '12px'
    },

    featureDescription: {
      fontSize: '0.95rem',
      color: '#64748b',
      lineHeight: '1.6'
    },

    pathSection: {
      maxWidth: '1200px',
      margin: '0 auto'
    },

    pathHeader: {
      textAlign: 'center',
      marginBottom: '60px'
    },

    pathTitle: {
      fontSize: '2.5rem',
      fontWeight: 'bold',
      color: '#1e293b',
      marginBottom: '16px',
      lineHeight: '1.2'
    },

    pathSubtitle: {
      fontSize: '1.1rem',
      color: '#64748b',
      maxWidth: '700px',
      margin: '0 auto',
      lineHeight: '1.6'
    },

    pathSteps: {
      display: 'grid',
      gridTemplateColumns: 'repeat(auto-fit, minmax(300px, 1fr))',
      gap: '40px'
    },

    stepCard: {
      background: 'white',
      borderRadius: '16px',
      padding: '30px',
      boxShadow: '0 4px 20px rgba(0, 0, 0, 0.05)',
      border: '1px solid #e2e8f0',
      position: 'relative',
      transition: 'all 0.3s ease'
    },

    stepNumber: {
      position: 'absolute',
      top: '-15px',
      left: '30px',
      background: 'linear-gradient(135deg, #3b82f6, #1d4ed8)',
      color: 'white',
      width: '40px',
      height: '40px',
      borderRadius: '50%',
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      fontSize: '16px',
      fontWeight: 'bold'
    },

    stepIcon: {
      fontSize: '24px',
      marginBottom: '16px'
    },

    stepTitle: {
      fontSize: '1.4rem',
      fontWeight: 'bold',
      color: '#1e293b',
      marginBottom: '12px'
    },

    stepDescription: {
      fontSize: '0.95rem',
      color: '#64748b',
      lineHeight: '1.6',
      marginBottom: '16px'
    },

    stepDuration: {
      display: 'inline-block',
      background: 'linear-gradient(135deg, #dbeafe, #bfdbfe)',
      color: '#3b82f6',
      padding: '4px 12px',
      borderRadius: '16px',
      fontSize: '0.85rem',
      fontWeight: '600'
    }
  };

  const features = [
    {
      icon: '🧠',
      iconStyle: styles.blueIcon,
      title: 'Comprehensive AI Training',
      description: 'Master cutting-edge artificial intelligence technologies through structured learning modules and hands-on projects.'
    },
    {
      icon: '🎯',
      iconStyle: styles.purpleIcon,
      title: 'Performance-Based Selection',
      description: 'Your dedication and results determine your path to exclusive internship opportunities with leading companies.'
    },
    {
      icon: '🌐',
      iconStyle: styles.greenIcon,
      title: 'Industry Partner Network',
      description: 'Access to internships with carefully vetted technology companies seeking exceptional AI talent.'
    },
    {
      icon: '🚀',
      iconStyle: styles.orangeIcon,
      title: 'Career Acceleration',
      description: 'Transform theoretical knowledge into practical expertise that drives real-world innovation and career growth.'
    }
  ];

  const pathSteps = [
    {
      number: '01',
      icon: '💻',
      title: 'Intensive Training',
      description: 'Complete our comprehensive AI curriculum covering machine learning, deep learning, and practical applications.',
      duration: '12 weeks'
    },
    {
      number: '02',
      icon: '📊',
      title: 'Performance Assessment',
      description: 'Demonstrate your expertise through rigorous evaluations, projects, and real-world problem-solving challenges.',
      duration: '2 weeks'
    },
    {
      number: '03',
      icon: '🏢',
      title: 'Internship Placement',
      description: 'Top performers gain access to exclusive internship opportunities with our prestigious partner companies.',
      duration: '6-12 months'
    }
  ];

  return (
    <div style={styles.container}>
      {/* Header Section */}
      <div style={styles.header}>
        <h1 style={styles.mainTitle}>
          Program <span style={styles.highlightedText}>Excellence</span>
        </h1>
        <p style={styles.subtitle}>
          A rigorous training program designed to identify and develop top-tier AI professionals for 
          industry leadership roles.
        </p>
      </div>

      {/* Features Grid */}
      <div style={styles.featuresGrid}>
        {features.map((feature, index) => (
          <div 
            key={index}
            style={styles.featureCard}
            onMouseEnter={(e) => {
              e.currentTarget.style.transform = 'translateY(-8px)';
              e.currentTarget.style.boxShadow = '0 12px 30px rgba(0, 0, 0, 0.1)';
            }}
            onMouseLeave={(e) => {
              e.currentTarget.style.transform = 'translateY(0)';
              e.currentTarget.style.boxShadow = '0 4px 20px rgba(0, 0, 0, 0.05)';
            }}
          >
            <div style={{...styles.featureIcon, ...feature.iconStyle}}>
              {feature.icon}
            </div>
            <h3 style={styles.featureTitle}>
              {feature.title}
            </h3>
            <p style={styles.featureDescription}>
              {feature.description}
            </p>
          </div>
        ))}
      </div>

      {/* Path to Success Section */}
      <div style={styles.pathSection}>
        <div style={styles.pathHeader}>
          <h2 style={styles.pathTitle}>
            Your Path to <span style={styles.highlightedText}>Success</span>
          </h2>
          <p style={styles.pathSubtitle}>
            A structured approach that transforms dedicated learners into industry-ready AI 
            professionals through proven methodology.
          </p>
        </div>

        <div style={styles.pathSteps}>
          {pathSteps.map((step, index) => (
            <div 
              key={index}
              style={styles.stepCard}
              onMouseEnter={(e) => {
                e.currentTarget.style.transform = 'translateY(-6px)';
                e.currentTarget.style.boxShadow = '0 12px 30px rgba(0, 0, 0, 0.1)';
              }}
              onMouseLeave={(e) => {
                e.currentTarget.style.transform = 'translateY(0)';
                e.currentTarget.style.boxShadow = '0 4px 20px rgba(0, 0, 0, 0.05)';
              }}
            >
              <div style={styles.stepNumber}>
                {step.number}
              </div>
              <div style={styles.stepIcon}>
                {step.icon}
              </div>
              <h3 style={styles.stepTitle}>
                {step.title}
              </h3>
              <p style={styles.stepDescription}>
                {step.description}
              </p>
              <span style={styles.stepDuration}>
                {step.duration}
              </span>
            </div>
          ))}
        </div>
      </div>

      {/* CSS for mobile responsiveness */}
      <style jsx>{`
        @media (max-width: 768px) {
          .mainTitle {
            font-size: 2.5rem;
          }
          
          .pathTitle {
            font-size: 2rem;
          }
          
          .featuresGrid {
            grid-template-columns: 1fr;
            gap: 20px;
          }
          
          .pathSteps {
            grid-template-columns: 1fr;
            gap: 30px;
          }
          
          .featureCard, .stepCard {
            padding: 20px;
          }
        }
        
        @media (max-width: 480px) {
          .mainTitle {
            font-size: 2rem;
          }
          
          .pathTitle {
            font-size: 1.75rem;
          }
          
          .subtitle, .pathSubtitle {
            font-size: 1rem;
          }
        }
      `}</style>
    </div>
  );
};

export default ProgramExcellence;